import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errMsg } from "../lib/api";

const Shell = ({ title, children }) => (
  <div className="wb-container grid min-h-[70vh] place-items-center py-16">
    <div className="wb-card w-full max-w-md p-8 sm:p-10 fade-up">
      <div className="eyebrow">Account</div>
      <h1 className="mt-3 font-display text-4xl tracking-tight">{title}</h1>
      {children}
    </div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setDone(data.message);
    } catch (e2) { setError(errMsg(e2)); }
    setBusy(false);
  };
  return (
    <Shell title="Forgot password">
      {done ? (
        <div className="mt-6" data-testid="forgot-success">
          <p className="text-sm leading-relaxed muted">{done}</p>
          <p className="mt-3 text-xs muted">The link is valid for 1 hour and can be used once.</p>
          <Link to="/login" className="btn-ghost mt-8" data-testid="forgot-back-to-login">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-form">
          <p className="text-sm muted">Enter your account email and we'll send you a link to choose a new password.</p>
          <div><label className="wb-label">Email</label><input type="email" required className="wb-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" /></div>
          {error && <p className="text-sm text-red-400" data-testid="forgot-error">{error}</p>}
          <button disabled={busy} className="btn-gold w-full" data-testid="forgot-submit-button">{busy ? "Sending…" : "Send reset link"}</button>
          <Link to="/login" className="block text-center text-xs muted hover:text-wb-gold">Back to sign in</Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [valid, setValid] = useState(null);
  const [f, setF] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!token) { setValid(false); return; }
    api.get("/auth/reset-password/validate", { params: { token } }).then((r) => setValid(r.data.valid)).catch(() => setValid(false));
  }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return setError("Passwords do not match");
    setBusy(true); setError("");
    try { await api.post("/auth/reset-password", { token, password: f.password }); setDone(true); }
    catch (e2) { setError(errMsg(e2)); }
    setBusy(false);
  };
  if (valid === null) return <Shell title="Reset password"><p className="mt-6 text-sm muted">Checking your link…</p></Shell>;
  if (done)
    return (
      <Shell title="Password updated">
        <p className="mt-6 text-sm muted" data-testid="reset-success">Your password has been changed and all other sessions were signed out.</p>
        <Link to="/login" className="btn-gold mt-8 w-full" data-testid="reset-go-login">Sign in</Link>
      </Shell>
    );
  if (!valid)
    return (
      <Shell title="Link expired">
        <p className="mt-6 text-sm muted" data-testid="reset-invalid">This reset link is invalid, already used, or has expired.</p>
        <Link to="/forgot-password" className="btn-gold mt-8 w-full" data-testid="reset-request-new">Request a new link</Link>
      </Shell>
    );
  return (
    <Shell title="Choose a new password">
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
        <div><label className="wb-label">New password (min 8)</label><input type="password" minLength={8} required className="wb-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="reset-password-input" /></div>
        <div><label className="wb-label">Confirm password</label><input type="password" minLength={8} required className="wb-input" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="reset-confirm-input" /></div>
        {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}
        <button disabled={busy} className="btn-gold w-full" data-testid="reset-submit-button">{busy ? "Saving…" : "Update password"}</button>
      </form>
    </Shell>
  );
}
